import { createSlice } from '@reduxjs/toolkit';

import {
  deleteLibraryItems,
  fetchLibraryItems,
  saveLibraryItems,
  tagLibraryItems,
  updateLibraryItem,
} from 'store/reducers/thunks/libraryThunks';

/*
The account's saved library: locations, vehicles and vehicle types kept between
scenarios.

Its own slice, deliberately. It is NOT scenario data:
  - `inputData` is wrapped in redux-undo, and an account fetch has no business
    in the undo history;
  - `inputData.present` is posted wholesale to /save_instance, so anything put
    there would be written into every saved scenario;
  - it has a server lifecycle (loading, failed, invalidated on sign-out) that
    the ui slice's remembered-switches state does not model.

Items are held in one flat list exactly as the server returns them, and split by
type in the selectors. One list means one status, and the server's answer is
never reshaped on the way in — so a field added to the record later arrives
without a change here.
*/

const initialState = {
  items: null, // null = never loaded; [] = loaded and empty. The picker needs to tell those apart.
  status: 'idle', // idle | loading | loaded | failed
  error: null,
};

const librarySlice = createSlice({
  name: 'library',
  initialState,
  reducers: {
    /* Sign-in, sign-out, or claiming an anonymous session: whoever we were
       holding items for is not who we are now. Back to idle so the next reader
       fetches, rather than briefly showing the previous account's library. */
    libraryInvalidated: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder.addCase(fetchLibraryItems.pending, (state) => {
      state.status = 'loading';
      state.error = null;
    });
    builder.addCase(fetchLibraryItems.fulfilled, (state, action) => {
      state.items = action.payload;
      state.status = 'loaded';
      state.error = null;
    });
    builder.addCase(fetchLibraryItems.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error?.message || 'Failed to load your library';
    });

    builder.addCase(saveLibraryItems.fulfilled, (state, action) => {
      // Only patch a list we actually hold: appending to null would claim the
      // library is "just these", and the next reader would never fetch the rest.
      if (state.items) state.items = [...state.items, ...action.payload];
    });

    builder.addCase(updateLibraryItem.fulfilled, (state, action) => {
      const updated = action.payload;
      if (!updated || !state.items) return;
      state.items = state.items.map(
        (item) => (item.item_id === updated.item_id ? updated : item));
    });

    builder.addCase(tagLibraryItems.fulfilled, (state, action) => {
      // Patch only what came back. Items whose call failed keep their old tags
      // rather than showing a change that never reached the server.
      const updated = action.payload?.updated;
      if (!updated?.length || !state.items) return;
      const byId = new Map(updated.map((item) => [item.item_id, item]));
      state.items = state.items.map((item) => byId.get(item.item_id) || item);
    });

    builder.addCase(deleteLibraryItems.fulfilled, (state, action) => {
      if (!state.items) return;
      const removed = new Set(action.payload);
      state.items = state.items.filter((item) => !removed.has(item.item_id));
    });
  },
});

export const { libraryInvalidated } = librarySlice.actions;
export default librarySlice.reducer;
